import React, { useCallback, useEffect, useLayoutEffect } from 'react';
import {
  Outlet,
  useLocation,
  useAsyncValue,
  useNavigate,
} from 'react-router-dom';
import { useTranslation } from 'react-i18next';

// Redux
import { useDispatch } from 'react-redux';
import { useSelector } from 'react-redux';
import { getNewActionTabsState } from '../../../../../state/selectors/ui/tabs';
import {
  actionTabs,
  actionTabsUnlockAll,
} from '../../../../../state/actions/ui/actionTabs';

// Components
import { SimpleWorkOrder } from '../../../../../components/display/ShowOT/SimpleWorkOrder';

// Styles
import { ButtonTab, TabsHalf, GeneralContainer, TabsContainer } from './styles';

// Types
import { BasicInfoOT } from './types';
import { Role } from '../../../../../types/auth';

// Hooks
import { useAuth } from '../../../../../hooks/useAuth';

// Utils
import { roleCanViewBudgets } from '../../../../../utils/rolePermissions';

export default function ActionPage(): JSX.Element {
  const { t } = useTranslation(['forms', 'workOrders']);
  const loaderData = useAsyncValue() as BasicInfoOT;
  const dispatch = useDispatch();
  const selector = useSelector(getNewActionTabsState);
  const location = useLocation();
  const pathNameArray = location.pathname.split('/');
  const pathName = pathNameArray[pathNameArray.length - 1];
  const navigate = useNavigate();
  const { user } = useAuth();

  const isSurveillanceRouteContext = location.pathname.startsWith(
    '/surveillance-route/'
  );

  useLayoutEffect(() => {
    if (!loaderData) {
      navigate('/work-orders');
    }
  }, []);

  useEffect(() => {
    // RV (2026-07-21): la Actuació básica YA existe al aterrizar aquí (P9 la
    // crea ANTES del redirect al wizard), así que es como el flujo de
    // validación —actuación existente—: todas las tabs navegables SIEMPRE,
    // robusto a recargar (actionTabs está en la blacklist de redux-persist y
    // se pierde en F5). En work-orders el flujo sigue secuencial (la
    // actuación no existe hasta el submit final). Espejo de
    // ActionsValidation/ActionPage.
    if (loaderData && isSurveillanceRouteContext) {
      dispatch(actionTabsUnlockAll());
    }
  }, [loaderData, isSurveillanceRouteContext]);

  useEffect(() => {
    // Habilitar automáticamente la tab cuando el usuario navega a ella
    if (!selector.includes(pathName)) {
      dispatch(actionTabs(pathName));
    }
  }, [location]);

  // Peso de cada mitad de la barra = cuántas pestañas pinta. Sin esto se
  // reparten 50/50 y una mitad con una sola pestaña se lleva media barra
  // (se vio al retirar Animals de la básica: «Materiales» ocupaba la mitad).
  const showBudgetItems =
    user?.user?.rolActive !== Role.Tablet &&
    roleCanViewBudgets(user?.user?.rolActive);
  const leftTabs =
    (isSurveillanceRouteContext ? 0 : 1) + 2 + (showBudgetItems ? 1 : 0);
  const rightTabs = isSurveillanceRouteContext ? 1 : 3;
  // 4 pestañas o menos caben en una fila: no hace falta partir la barra en
  // tablet, y partirla dejaba a «Materiales» sola en una fila entera.
  const forceRow = leftTabs + rightTabs <= 4;

  const onClickCheckDisabled = useCallback(
    (path: string) => (e: { preventDefault: () => void }) => {
      if (!selector.includes(path)) e.preventDefault();
    },
    [selector]
  );

  return (
    <GeneralContainer>
      {loaderData && (
        <SimpleWorkOrder
          name={t('action.basicInfo')}
          pkInit={loaderData.pkInit}
          pkEnd={loaderData.pkEnd}
          orderDescription={loaderData.orderDescription}
          actionDescription={loaderData.actionDescription}
        />
      )}
      <TabsContainer $forceRow={forceRow}>
        <TabsHalf
          $tabCount={leftTabs}
          $wideFirstTab={!isSurveillanceRouteContext}
          $forceRow={forceRow}
        >
          {/* Timer iteration 2026-04-15: hide operators tab in SR context —
              operators/vehicles are inherited from the parent surveillance route */}
          {!location.pathname.startsWith('/surveillance-route/') && (
            <ButtonTab
              to={'operators-and-vehicles'}
              disabled={!selector.includes('operators-and-vehicles')}
            >
              {t('workOrders:tabs.operatorsAndVehicles')}
            </ButtonTab>
          )}
          <ButtonTab
            onClick={onClickCheckDisabled('location')}
            to={'location'}
            disabled={!selector.includes('location')}
          >
            {t('workOrders:tabs.location')}
          </ButtonTab>
          <ButtonTab
            onClick={onClickCheckDisabled('edition')}
            to={'edition'}
            disabled={!selector.includes('edition')}
          >
            {t('workOrders:tabs.actionData')}
          </ButtonTab>
          {showBudgetItems && (
              <ButtonTab
                onClick={onClickCheckDisabled('budget-items')}
                to={'budget-items'}
                disabled={!selector.includes('budget-items')}
              >
                {t('workOrders:budgetItems')}
              </ButtonTab>
            )}
        </TabsHalf>
        <TabsHalf
          $tabCount={rightTabs}
          $wideSecondTab={!isSurveillanceRouteContext}
          $forceRow={forceRow}
        >
          <ButtonTab
            onClick={onClickCheckDisabled('materials')}
            to={'materials'}
            disabled={!selector.includes('materials')}
          >
            {t('workOrders:tabs.materials')}
          </ButtonTab>
          {/* V4 FR-9.3: Maquinària Auxiliar oculta en SR — operaris/vehicles
              heredados del recorrido, así que la maquinaria no aplica. */}
          {!location.pathname.startsWith('/surveillance-route/') && (
            <ButtonTab
              onClick={onClickCheckDisabled('machinery')}
              to={'machinery'}
              disabled={!selector.includes('machinery')}
            >
              {t('workOrders:tabs.machinery')}
            </ButtonTab>
          )}
          {/* Fauna fuera de la Actuació bàsica (2026-08-11): la pestaña y sus
              datos no se usan en el recorrido, así que Materials pasa a ser el
              último paso del asistente y lleva el botón de guardar. */}
          {!isSurveillanceRouteContext && (
            <ButtonTab
              onClick={onClickCheckDisabled('animals')}
              to={'animals'}
              disabled={!selector.includes('animals')}
            >
              {t('workOrders:tabs.animals')}
            </ButtonTab>
          )}
        </TabsHalf>
      </TabsContainer>
      <Outlet />
    </GeneralContainer>
  );
}
